import { CSSProperties } from "react";
import { Divider, Spin, Typography } from "antd";
import { numberFormatInt } from "utils/nuberFormat";
import { useFactoryCodCreditInfo } from "hooks/useFactoryCredit";

/** ข้อความเดียวกับระบบ parcel (ดู ParcelShared/InsufficientCreditModal.tsx) ปรับคำให้ตรงกับบริบท COD
 * export ไว้ให้ใช้ร่วมกับ modal "เติมเครดิต" ที่แถบเครื่องมือ bulk-accept (OrderTabsCard/index.tsx) ด้วย */
export const INSUFFICIENT_CREDIT_LINES = [
  "เครดิตคงเหลือของท่านไม่เพียงพอสำหรับการรับคำสั่งซื้อ COD",
  "ติดต่อเจ้าหน้าที่ (คุณจอย) ที่เบอร์โทรศัพท์ 0868263999 โดยแจ้งความประสงค์ต้องการเติม Credit สำหรับรับคำสั่งซื้อ COD",
];

/** โรงงานมียอดหนี้เครดิต (available_credit < 0) — ห้ามรับ COD จนกว่าจะเติมชำระหนี้ */
export const CREDIT_DEBT_BLOCKED_LINES = [
  "มียอดหนี้เครดิตค้างชำระ — ไม่สามารถรับคำสั่งซื้อ COD ได้จนกว่าจะเติมเครดิตชำระหนี้",
  "ติดต่อเจ้าหน้าที่ (คุณจอย) ที่เบอร์โทรศัพท์ 0868263999 โดยแจ้งความประสงค์ต้องการเติม Credit",
];

/**
 * คอลัมน์ grid แชร์ระหว่าง card "สรุปรายการคำสั่งซื้อ" กับ card เครดิต COD ใน BulkAccept
 * (label | "จำนวน" | ค่า | หน่วย)
 *
 * ใช้ 1fr + ความกว้างคงที่ของคอลัมน์ค่า/หน่วย — ไม่ใช้ minmax(..., auto) ที่คอลัมน์ label
 * เพราะแต่ละการ์ดจะขยายตามข้อความยาวสุดคนละแบบ ทำให้คำว่า "จำนวน" เลื่อนไม่ตรงแนวตั้ง
 */
export const BULK_ACCEPT_QTY_SUMMARY_GRID_COLUMNS =
  "1fr max-content 6.5rem 3.5rem";

export const BULK_ACCEPT_QTY_SUMMARY_GRID_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: BULK_ACCEPT_QTY_SUMMARY_GRID_COLUMNS,
  columnGap: 12,
  rowGap: 6,
  alignItems: "baseline",
};

/**
 * แสดงวงเงินเครดิต COD คงเหลือ + ต้นทุนที่ต้องใช้สำหรับรับออเดอร์นี้ (แค่แสดงผลก่อนตัดสินใจ —
 * การตรวจสอบ/จองเครดิตจริงเกิดที่ backend ตอนกดยืนยันรับคำสั่งซื้อ)
 * variant "card" (BulkAcceptNewOrdersModal รับหลายรายการ) เป็น card (ขอบ/พื้นเทาอ่อน) — grid ใช้
 * BULK_ACCEPT_QTY_SUMMARY_GRID_STYLE ร่วมกับ card "สรุปรายการคำสั่งซื้อ" ด้านบน (4 คอลัมน์:
 * หัวข้อ/จำนวน/ค่า/หน่วย — ของเครดิตไม่มีหน่วยเลยเว้นคอลัมน์สุดท้ายว่างไว้ให้ครบ 4 ช่องต่อแถวเหมือนกัน)
 * variant "plain" (ModalOrderDetail รับเดี่ยว) ไม่มี card ครอบ มีแค่เส้นแบ่งด้านบนแทน
 * ข้อความเครดิตไม่พอใช้คำเดียวกับระบบ parcel ทั้งสอง variant
 */
const CodCreditSummary = ({
  loading,
  availableCredit,
  remainingCredit,
  cost,
  sufficient,
  hasCreditDebt,
  fontSize,
  showQuantityLabel,
  costLabel,
  variant = "card",
}: {
  loading: boolean;
  /**
   * quota_before_selection (= remaining_quota + ยอดที่ออเดอร์/ชุดออเดอร์นี้จองไปแล้ว) — ทั้ง
   * CodCreditInfoSingle และ CodCreditInfoBulk ส่งค่านี้เหมือนกัน (2026-08-11: ตกลงใช้สูตรเดียวกันไปก่อน
   * แม้ CodCreditInfoSingle จะแสดงคู่กับ OrderCodCreditCard มุมบนขวาที่โชว์ remaining_quota ตรงๆ ทำให้
   * ตัวเลขสองป้าย "เครดิตที่ใช้ได้" ไม่ตรงกันบนจอเดียวกัน — ยอมรับความไม่ตรงนี้ไปก่อน)
   */
  availableCredit?: number;
  /** โควต้าหลังชุดนี้ (quota_after_selection) */
  remainingCredit?: number;
  cost?: number | null;
  sufficient?: boolean | null;
  /** มียอดหนี้เครดิตค้างชำระ — ข้อความเตือนแยกจาก insufficient ต่อออเดอร์ */
  hasCreditDebt?: boolean;
  fontSize?: number;
  /** แสดงคำว่า "จำนวน" คั่นกลาง + คอลัมน์หน่วยว่าง เหมือน card สรุปรายการคำสั่งซื้อด้านบน (bulk) */
  showQuantityLabel?: boolean;
  /** ป้ายแถวต้นทุน — bulk ใช้ "คำสั่งซื้อที่เลือก" */
  costLabel?: string;
  /** "card" = มีขอบ/พื้นเทาอ่อน (bulk), "plain" = ไม่มี card มีเส้นแบ่งด้านบนแทน (ModalOrderDetail) */
  variant?: "card" | "plain";
}) => {
  const content = (
    <>
      <Typography.Text strong style={{ display: "block", marginBottom: 6, fontSize }}>
        เครดิตสำหรับคำสั่งซื้อ COD
      </Typography.Text>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Spin size="small" />
          <Typography.Text type="secondary" style={{ fontSize }}>
            กำลังตรวจสอบวงเงินเครดิต COD...
          </Typography.Text>
        </div>
      ) : (
        <>
          <div
            style={
              showQuantityLabel
                ? { ...BULK_ACCEPT_QTY_SUMMARY_GRID_STYLE, fontSize }
                : {
                    display: "grid",
                    gridTemplateColumns: "1fr minmax(6.5rem, max-content)",
                    columnGap: 12,
                    rowGap: 6,
                    alignItems: "baseline",
                    fontSize,
                  }
            }
          >
{/*
            <Typography.Text style={{ fontWeight: "400", fontSize }}>เครดิตที่ใช้ได้</Typography.Text>
            {showQuantityLabel && <Typography.Text type="secondary">จำนวน</Typography.Text>}
            <Typography.Text style={{ fontSize, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {numberFormatInt(availableCredit ?? 0)}
            </Typography.Text>
            {showQuantityLabel && <Typography.Text />}
*/}
            <Typography.Text style={{ fontWeight: "400", fontSize }}>
              {costLabel ?? "เครดิตที่ใช้สำหรับคำสั่งซื้อนี้"}
            </Typography.Text>
            {showQuantityLabel && <Typography.Text type="secondary">จำนวน</Typography.Text>}
            <Typography.Text
              type={sufficient === false ? "danger" : undefined}
              style={{ fontSize, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
            >
              {numberFormatInt(cost ?? 0)}
            </Typography.Text>
            {showQuantityLabel && <Typography.Text />}
{/*}
            <Typography.Text style={{ fontWeight: "400", fontSize }}>คงเหลือ</Typography.Text>
            {showQuantityLabel && <Typography.Text type="secondary">จำนวน</Typography.Text>}
            <Typography.Text
              type={sufficient === false ? "danger" : undefined}
              style={{ fontSize, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
            >
              {numberFormatInt(remainingCredit ?? 0)}
            </Typography.Text>
            */}
            {showQuantityLabel && <Typography.Text />}
            
          </div>
          {sufficient === false && (
            <div
              style={{
                marginTop: 10,
                border: "1px solid #ffccc7",
                borderRadius: 8,
                padding: "10px 12px",
                backgroundColor: "#fff2f0",
              }}
            >
              {(hasCreditDebt ? CREDIT_DEBT_BLOCKED_LINES : INSUFFICIENT_CREDIT_LINES).map((line) => (
                <Typography.Text key={line} type="danger" style={{ display: "block", fontSize }}>
                  {line}
                </Typography.Text>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );

  if (variant === "plain") {
    return (
      <>
        <Divider style={{ margin: "8px 0" }} />
        <div style={{ padding: 10 }}>{content}</div>
      </>
    );
  }

  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: "14px 16px",
        backgroundColor: "#fafafa",
      }}
    >
      {content}
    </div>
  );
};

export const CodCreditInfoSingle = ({ orderId, enabled }: { orderId: string; enabled: boolean }) => {
  const { data, isLoading } = useFactoryCodCreditInfo({ orderId }, { enabled });
  if (!enabled) return null;
  return (
    <CodCreditSummary
      loading={isLoading}
      availableCredit={data?.quota_before_selection ?? data?.remaining_quota ?? data?.usable_credit}
      remainingCredit={data?.quota_after_selection ?? data?.remaining_quota}
      cost={data?.cost_for_order}
      sufficient={data?.sufficient}
      hasCreditDebt={(data?.insufficient_order_ids?.length ?? 0) > 0}
      variant="plain"
    />
  );
};

export const CodCreditInfoBulk = ({
  orderIds,
  enabled,
  fontSize,
}: {
  orderIds: string[];
  enabled: boolean;
  /** ส่งมาจาก BulkAcceptNewOrdersModal's BULK_ACCEPT_SUMMARY_FONT_SIZE ให้ตัวอักษรเท่ากับ datatable เสมอ */
  fontSize?: number;
}) => {
  const { data, isLoading } = useFactoryCodCreditInfo({ orderIds }, { enabled: enabled && orderIds.length > 0 });
  if (!enabled || orderIds.length === 0) return null;
  return (
    <CodCreditSummary
      loading={isLoading}
      availableCredit={data?.quota_before_selection ?? data?.remaining_quota ?? data?.usable_credit}
      remainingCredit={data?.quota_after_selection ?? data?.remaining_quota}
      cost={data?.total_cost}
      sufficient={data?.sufficient}
      hasCreditDebt={(data?.insufficient_order_ids?.length ?? 0) > 0}
      fontSize={fontSize}
      showQuantityLabel
      costLabel="เครดิตที่ใช้สำหรับคำสั่งซื้อที่เลือก"
    />
  );
};
